import queryString from 'qs'
import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'

import type {Amount} from '@pleo-io/deimos'

import {useLoggedInUser} from '~/shell/user'

import {getDeimos} from './helpers'

import type {
    WalletActivityBlockedReason,
    WalletActivityStatus,
    WalletActivityType,
} from '../types/wallet'

export interface WalletActivity {
    id: string
    companyId: string
    referenceId: string
    amount: Amount
    status: WalletActivityStatus
    isAutomatic: boolean
    type: WalletActivityType
    activityUpdatedAt: string | Date | number
    loadScheduledAt?: string | Date | number
    blockedReason?: WalletActivityBlockedReason
}

export type WalletActivityResponse = {
    result: WalletActivity[]
    totalItems: number
}

export function useWalletActivities(status?: WalletActivityStatus, limit = 20) {
    const user = useLoggedInUser()
    const result = useSWRInfinite<WalletActivityResponse, Error>(
        (pageIndex, previousPageData) => {
            if (!user?.companyId || (previousPageData && !previousPageData.result.length)) {
                return null
            }

            const queryParams = queryString.stringify({status, limit, offset: pageIndex * limit})

            return `/wallet-management/rest/v4/companies/${user.companyId}/wallet-activities?${queryParams}`
        },
        getDeimos,
        {revalidateIfStale: false},
    )

    const dataFlattened = (result.data ?? [])
        .map((d) => d.result)
        .flat()
        .map((d) => ({...d, id: d.referenceId}))

    const totalItemsCount = result.data?.[0]?.totalItems ?? 0

    const canFetchMore =
        result.data?.[result.data?.length - 1].result?.length === limit &&
        dataFlattened.length !== result.data[0].totalItems

    const isLoadingMore =
        result.size > 0 && result.data && typeof result.data[result.size - 1] === 'undefined'

    const loadMore = () => {
        result.setSize?.((result.size ?? 0) + 1)
    }

    return {
        ...result,
        data: dataFlattened,
        totalItemsCount,
        canFetchMore,
        isLoadingMore,
        isLoadingInitialData: !result.data && !result.error,
        mutations: {
            loadMore,
        },
    }
}

export function useWalletActivitiesPreview(limit = 20) {
    const user = useLoggedInUser()
    const queryParams = queryString.stringify({limit, offset: 0})

    const result = useSWR<WalletActivityResponse, Error>(
        user?.companyId
            ? `/wallet-management/rest/v4/companies/${user.companyId}/wallet-activities?${queryParams}`
            : null,
        getDeimos,
        {revalidateIfStale: false},
    )

    return {
        ...result,
        data: result.data?.result,
    }
}
